import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  startTransition,
  ReactNode,
} from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Chip, Box } from '@mui/material';
import { heIL } from '@mui/material/locale';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};

const lightTheme = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
    secondary: {
      main: '#64748b',
      light: '#94a3b8',
      dark: '#475569',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: ['Rubik', 'Assistant', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'].join(','),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          background-color: #f8fafc;
        }
        
        /* RTL Label positioning in notched outline */
        fieldset legend {
          float: right;
          text-align: right;
        }
        
        fieldset legend span {
          padding-left: 12px;
          padding-right: 12px;
        }
      `,
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&.edit-action': {
            color: '#2563eb',
            '&:hover': {
              backgroundColor: '#dbeafe',
            }
          },
          '&.delete-action': {
            color: '#ef4444',
            '&:hover': {
              backgroundColor: '#fee2e2',
            }
          },
          '&.print-action, &.print-alt-action': {
            color: '#10b981',
            '&:hover': {
              backgroundColor: '#d1fae5',
            }
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.875rem',
            backgroundColor: '#f8fafc',
            color: '#475569',
            borderBottom: '2px solid #e2e8f0',
            padding: '16px',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          textAlign: 'center',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            direction: 'rtl',
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2563eb',
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          textAlign: 'right',
          paddingRight: '14px',
          '&::placeholder': { textAlign: 'right', opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          right: 0,
          left: 'auto',
          transformOrigin: 'top right',
          paddingRight: '32px',
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            transform: 'translate(10px, -9px) scale(0.75)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          direction: 'rtl',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          right: 0,
          left: 'auto',
          transformOrigin: 'top right',
          maxWidth: 'calc(100% - 32px)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          textAlign: 'right',
          paddingRight: '14px',
          paddingLeft: '40px',
          direction: 'rtl',
        },
        icon: {
          left: '7px',
          right: 'auto',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
        disableClearable: true,
        limitTags: -1,
        ChipProps: { size: 'small' as const },
        slotProps: {
          popper: {
            placement: 'bottom-start',
            modifiers: [{ name: 'popper', options: { strategy: 'fixed' as const } }],
          },
        },
        renderTags: (value: unknown[], getTagProps: (params: { index: number }) => object) => {
          if (!value?.length) return [];
          const getLabel = (item: unknown) =>
            typeof item === 'object' && item !== null && 'label' in item
              ? String((item as { label: string }).label)
              : String(item);
          const chips = value.map((item, index) =>
            React.createElement(Chip, {
              ...getTagProps({ index }),
              key: index,
              label: getLabel(item),
              size: 'small',
            }),
          );
          const maxVisible = 3;
          if (value.length <= maxVisible) return chips;
          const visible = chips.slice(0, maxVisible);
          const more = value.length - maxVisible;
          return [
            ...visible,
            React.createElement(
              Box,
              {
                component: 'span',
                key: 'more',
                sx: { color: 'text.secondary', fontSize: '0.875rem', ml: 0.5, flexShrink: 0 },
              },
              `+${more}`,
            ),
          ];
        },
      },
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-inputRoot': {
            paddingRight: '14px !important',
            paddingLeft: '45px !important',
            direction: 'rtl',
            minHeight: 40,
            alignItems: 'center',
            justifyContent: 'flex-start',
            overflow: 'hidden',
          },
          '&:has(.MuiAutocomplete-tag) .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
            overflow: 'hidden',
            alignItems: 'center',
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingLeft: '60px !important',
            minHeight: 40,
            gap: '6px',
          },
          '&:has(.MuiAutocomplete-tag) .MuiInputAdornment-root': {
            maxWidth: 'calc(100% - 70px)',
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiOutlinedInput-root': {
            overflow: 'hidden',
          },
          '& .MuiAutocomplete-input': {
            overflow: 'hidden',
            minWidth: 0,
          },
          '& .MuiAutocomplete-tag': {
            maxWidth: '180px',
            minWidth: 0,
            flexShrink: 0,
            margin: 0,
          },
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .MuiInputBase-input': {
            flex: '1 1 0%',
            minWidth: 80,
            textAlign: 'right !important',
            direction: 'rtl',
            paddingRight: 0,
            paddingLeft: 0,
            '&::placeholder': { textAlign: 'right', opacity: 1 },
          },
          /* שדות ללא בחירה מרובה – בלי padding כפול על ה-input (ה-root כבר עם paddingRight 14px) */
          '&:not(:has(.MuiAutocomplete-tag)) .MuiAutocomplete-inputRoot': {
            position: 'relative',
            '& .MuiAutocomplete-input': {
              paddingRight: 0,
            },
          },
          '&:not(:has(.MuiAutocomplete-tag)) .MuiAutocomplete-clearIndicator': {
            position: 'absolute !important',
            right: '10px !important',
            left: 'auto !important',
            margin: 0,
          },
        },
        endAdornment: {
          right: 'auto !important',
          left: '9px !important',
          display: 'flex !important',
          gap: '8px !important',
        },
        popupIndicator: {
          right: 'auto !important',
          left: '7px !important',
        },
        clearIndicator: {
          right: '8px !important',
          left: 'auto !important',
        },
      },
    },
  },
}, heIL);

const darkTheme = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'dark',
    primary: {
      main: '#60a5fa',
      light: '#93c5fd',
      dark: '#3b82f6',
    },
    secondary: {
      main: '#94a3b8',
      light: '#cbd5e1',
      dark: '#64748b',
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
    },
  },
  typography: {
    fontFamily: ['Rubik', 'Assistant', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'].join(','),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          background-color: #0f172a;
        }
        
        /* RTL Label positioning in notched outline */
        fieldset legend {
          float: right;
          text-align: right;
        }
        
        fieldset legend span {
          padding-left: 12px;
          padding-right: 12px;
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          color: '#cbd5e1',
          borderColor: 'rgba(148, 163, 184, 0.45)',
          backgroundColor: 'rgba(15, 23, 42, 0.3)',
          '&:hover': {
            borderColor: 'rgba(148, 163, 184, 0.7)',
            backgroundColor: 'rgba(51, 65, 85, 0.55)',
          },
          '&.Mui-disabled': {
            color: 'rgba(148, 163, 184, 0.45)',
            borderColor: 'rgba(71, 85, 105, 0.55)',
            backgroundColor: 'rgba(15, 23, 42, 0.18)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&.edit-action': {
            color: '#60a5fa',
            '&:hover': {
              backgroundColor: 'rgba(96, 165, 250, 0.1)',
            }
          },
          '&.delete-action': {
            color: '#f87171',
            '&:hover': {
              backgroundColor: 'rgba(248, 113, 113, 0.1)',
            }
          },
          '&.print-action, &.print-alt-action': {
            color: '#34d399',
            '&:hover': {
              backgroundColor: 'rgba(52, 211, 153, 0.1)',
            }
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.875rem',
            backgroundColor: '#334155',
            color: '#f1f5f9',
            borderBottom: '2px solid #475569',
            padding: '16px',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          textAlign: 'center',
          borderColor: '#334155',
        },
        body: {
          color: '#f1f5f9',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:nth-of-type(odd)': {
            backgroundColor: 'rgba(30, 41, 59, 0.35)',
          },
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: '#1e293b',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            direction: 'rtl',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            '& fieldset': {
              borderColor: '#334155',
            },
            '&:hover fieldset': {
              borderColor: '#475569',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#60a5fa',
            },
            '& input': {
              color: '#f1f5f9',
            },
            '& input::placeholder': {
              color: '#94a3b8',
              opacity: 1,
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e293b',
          color: '#f1f5f9',
          '& input': {
            color: '#f1f5f9',
          },
          '& input::placeholder': {
            color: '#94a3b8',
            opacity: 1,
          },
          '&.Mui-focused': {
            backgroundColor: '#1e293b',
          },
        },
        input: {
          textAlign: 'right',
          paddingRight: '14px',
          color: '#f1f5f9',
          '&::placeholder': {
            color: '#94a3b8',
            opacity: 1,
            textAlign: 'right',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          right: 0,
          left: 'auto',
          transformOrigin: 'top right',
          paddingRight: '32px',
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            transform: 'translate(10px, -9px) scale(0.75)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          direction: 'rtl',
          backgroundColor: '#1e293b',
          color: '#f1f5f9',
          '& fieldset': {
            borderColor: '#334155',
          },
          '&:hover fieldset': {
            borderColor: '#475569',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#60a5fa',
          },
          '& input': {
            color: '#f1f5f9',
          },
          '& input::placeholder': {
            color: '#94a3b8',
            opacity: 1,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          right: 0,
          left: 'auto',
          transformOrigin: 'top right',
          maxWidth: 'calc(100% - 32px)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e293b',
          color: '#f1f5f9',
          '& fieldset': {
            borderColor: '#334155',
          },
          '&:hover fieldset': {
            borderColor: '#475569',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#60a5fa',
          },
        },
        select: {
          textAlign: 'right',
          paddingRight: '14px',
          paddingLeft: '40px',
          direction: 'rtl',
          color: '#f1f5f9',
          backgroundColor: '#1e293b',
        },
        icon: {
          left: '7px',
          right: 'auto',
          color: '#94a3b8',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
        disableClearable: true,
        limitTags: -1,
        ChipProps: { size: 'small' as const },
        slotProps: {
          popper: {
            placement: 'bottom-start',
            modifiers: [{ name: 'popper', options: { strategy: 'fixed' as const } }],
          },
        },
        renderTags: (value: unknown[], getTagProps: (params: { index: number }) => object) => {
          if (!value?.length) return [];
          const getLabel = (item: unknown) =>
            typeof item === 'object' && item !== null && 'label' in item
              ? String((item as { label: string }).label)
              : String(item);
          const chips = value.map((item, index) =>
            React.createElement(Chip, {
              ...getTagProps({ index }),
              key: index,
              label: getLabel(item),
              size: 'small',
            }),
          );
          const maxVisible = 3;
          if (value.length <= maxVisible) return chips;
          const visible = chips.slice(0, maxVisible);
          const more = value.length - maxVisible;
          return [
            ...visible,
            React.createElement(
              Box,
              {
                component: 'span',
                key: 'more',
                sx: { color: 'text.secondary', fontSize: '0.875rem', ml: 0.5, flexShrink: 0 },
              },
              `+${more}`,
            ),
          ];
        },
      },
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-inputRoot': {
            paddingRight: '14px !important',
            paddingLeft: '45px !important',
            direction: 'rtl',
            minHeight: 40,
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            '& fieldset': {
              borderColor: '#334155',
            },
            '&:hover fieldset': {
              borderColor: '#475569',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#60a5fa',
            },
            '& input': {
              color: '#f1f5f9',
            },
            '& input::placeholder': {
              color: '#94a3b8',
              opacity: 1,
            },
          },
          '&:has(.MuiAutocomplete-tag) .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
            overflow: 'hidden',
            alignItems: 'center',
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingLeft: '60px !important',
            minHeight: 40,
            gap: '6px',
          },
          '&:has(.MuiAutocomplete-tag) .MuiInputAdornment-root': {
            maxWidth: 'calc(100% - 70px)',
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiOutlinedInput-root': {
            overflow: 'hidden',
          },
          '& .MuiAutocomplete-input': {
            overflow: 'hidden',
            minWidth: 0,
          },
          '& .MuiAutocomplete-tag': {
            maxWidth: '180px',
            minWidth: 0,
            flexShrink: 0,
            margin: 0,
          },
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .MuiInputBase-input': {
            flex: '1 1 0%',
            minWidth: 80,
            textAlign: 'right !important',
            direction: 'rtl',
            paddingRight: 0,
            paddingLeft: 0,
            color: '#f1f5f9',
            '&::placeholder': { textAlign: 'right', opacity: 1 },
          },
          '&:not(:has(.MuiAutocomplete-tag)) .MuiAutocomplete-inputRoot': {
            position: 'relative',
            '& .MuiAutocomplete-input': { paddingRight: 0 },
          },
          '&:not(:has(.MuiAutocomplete-tag)) .MuiAutocomplete-clearIndicator': {
            position: 'absolute !important',
            right: '10px !important',
            left: 'auto !important',
            margin: 0,
          },
        },
        endAdornment: {
          right: 'auto !important',
          left: '9px !important',
          display: 'flex !important',
          gap: '8px !important',
        },
        popupIndicator: {
          right: 'auto !important',
          left: '7px !important',
          color: '#94a3b8',
        },
        clearIndicator: {
          right: '8px !important',
          left: 'auto !important',
          color: '#94a3b8',
        },
      },
    },
  },
}, heIL);

interface ThemeModeProviderProps {
  children: ReactNode;
  /** ברירת מחדל כשאין העדפה שמורה — מעקב תיקונים: dark, מערכת המעבדה: light */
  defaultMode?: ThemeMode;
  /** מפתח localStorage נפרד לכל אזור באפליקציה */
  storageKey?: string;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({
  children,
  defaultMode = 'light',
  storageKey = 'themeMode',
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === 'dark' || saved === 'light' ? saved : defaultMode;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, mode);
  }, [mode, storageKey]);

  const toggleMode = useCallback(() => {
    startTransition(() => {
      setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
    });
  }, []);

  const theme = mode === 'dark' ? darkTheme : lightTheme;
  const contextValue = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

